import React from 'react';
import ApiProvider from '../../../core/api/ApiProvider';
import * as Yup from 'yup';
import { Form as FormikForm, Formik } from 'formik';
import { Alert, AreaField, Button, Form, Loader, SelectField } from '../../../core/components';
import { InfoMessageService, TranslationService } from '../../../core/services';
import { DictionaryType } from '../../../core/types/DictionaryType';
import { ResponseApiType } from '../../types/ResponseApiType';
import { ApplyForDelayResponseType } from './types/ApplyForDealyResponseType';
import styles from './ApplyForDelay.module.scss';
import { useMutation, useQuery } from 'react-query';
import { ActionStatusType } from '../../../core/types/ActionStatusType';
import { useParams } from 'react-router-dom';

type FormValuesType = {
  settlementPeriodId: string;
  comment: string;
};

type Props = {
  onClose: (isOpenInstallmentModal: boolean) => void;
};

const validationSchema = Yup.object().shape({
  settlementPeriodId: Yup.string().required(TranslationService.translate('RequiredField')),
  comment: Yup.string().required(TranslationService.translate('RequiredField')),
});

const ApplyForDelay = (props: Props) => {
  const { caseNumber, tenantUId } = useParams() as { caseNumber: string; tenantUId: string };
  const [weeks, setWeeks] = React.useState<DictionaryType<string>[]>([]);

  const applyForDelaySettings = useQuery<ActionStatusType<ApplyForDelayResponseType>>(
    ['applyForDelaySettings', caseNumber],
    () =>
      ApiProvider.post<ResponseApiType<ApplyForDelayResponseType>>(
        'ApplyForDelayModule/GetApplyForDelaySettings',
        { caseNo: caseNumber, sourceTenantId: tenantUId }
      ),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      onSuccess: (data) => {
        if (data.success) {
          const weeksDelayArray = [];

          for (let i = 1; i <= 4; i++) {
            const item = {
              key: `${i}`,
              value: `${i} ${TranslationService.translate('Week')}`,
            };

            weeksDelayArray.push(item);
          }

          setWeeks(weeksDelayArray);
        } else {
          InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
        }
      },
      onError: () => {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      },
    }
  );

  const delayOptions = [
    {
      key: '1',
      value: `1 ${TranslationService.translate('Week')}`,
    },
  ];
  const applyForDelay = useMutation(
    (values: DictionaryType<string>) => {
      return ApiProvider.post<ResponseApiType>(`ApplyForDelayModule/${values.path}`, {
        caseNo: caseNumber,
        numOfWeeks: parseFloat(values.settlementPeriodId),
        comment: values.comment,
        sourceTenantId: tenantUId,
      });
    },
    {
      onSuccess: (data) => {
        props.onClose(false);

        if (data.success) {
          InfoMessageService.success(
            TranslationService.translate('ApplyForDelayRequestSendSuccessfully')
          );
        } else {
          InfoMessageService.error(data.viewMessage);
        }
      },
      onError: () => {
        InfoMessageService.error(TranslationService.translate('ErrorSthWentWrong'));
      },
    }
  );

  const handleSendForm = (values: FormValuesType) => {
    if (applyForDelaySettings && applyForDelaySettings.data) {
      applyForDelay.mutate({
        settlementPeriodId: values.settlementPeriodId,
        comment: values.comment,
        caseNumber: caseNumber,
        sourceTenantId: tenantUId,
        path: 'ApplyForDelay',
      });
    }
  };

  return (
    <>
      {applyForDelaySettings.isLoading || applyForDelaySettings.isFetching ? (
        <Loader />
      ) : (
        applyForDelaySettings &&
        applyForDelaySettings.data?.success &&
        (!applyForDelaySettings.data.data.isValid ? (
          <Alert variant={Alert.Variants.Info} className="mb-2">
            {applyForDelaySettings.data.message}
            <br />
          </Alert>
        ) : (
          <>
            <Alert variant={Alert.Variants.Info} className="mb-2">
              {TranslationService.translate('PostponementMsg')}
              <br />
              <a
                href="#installments"
                onClick={() => {
                  props.onClose(true);
                }}
              >
                {TranslationService.translate('ProlongmentMsg')}
              </a>
            </Alert>

            <Formik
              initialValues={{
                settlementPeriodId: delayOptions[0].key,
                comment: '',
              }}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                handleSendForm(values);
              }}
            >
              {({ values, handleChange, isValid, errors }) => (
                <FormikForm className={styles.formWrapper}>
                  <Form.Group className="mb-2">
                    {applyForDelaySettings && (
                      <SelectField
                        id="settlementPeriodId"
                        name="settlementPeriodId"
                        ariaLabel={TranslationService.translate('SettlementPeriod')}
                        value={values.settlementPeriodId}
                        options={
                          weeks &&
                          weeks.map((week) => ({
                            name: week.value,
                            value: week.key,
                          }))
                        }
                        onChange={(value, e) => {
                          handleChange(e);
                        }}
                      />
                    )}
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      <span
                        style={{
                          whiteSpace: 'break-spaces',
                          lineHeight: 1.3,
                        }}
                      >
                        {TranslationService.translate('Comment')}
                      </span>
                    </Form.Label>

                    <AreaField
                      name="comment"
                      id="applyfordelay-commentfield"
                      ariaLabel={TranslationService.translate('Comment')}
                      cols={30}
                      rows={4}
                      errors={errors.comment}
                      className="form-control textarea-lg"
                      value={values.comment}
                      onChange={(value, e) => {
                        handleChange(e);
                      }}
                    />
                  </Form.Group>

                  <Button
                    id="SendApplyForDelay"
                    type={Button.Types.Submit}
                    variant={Button.Variants.Primary}
                    disabled={!isValid || applyForDelay.isLoading}
                    inProgress={applyForDelay.isLoading}
                    ariaLabel={TranslationService.translate('Send')}
                  >
                    {TranslationService.translate('Send')}
                  </Button>
                </FormikForm>
              )}
            </Formik>
          </>
        ))
      )}
    </>
  );
};

export default ApplyForDelay;
